import { CommonDialogs } from "@mcleod/common";
import { BlurEvent, Button, DataSource, DataSourceMode, DropdownItem, EditRowDecorator, Textbox, Toast } from "@mcleod/components";
import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutTariffRegionZipCodeSlideOut } from "./autogen/AutogenLayoutTariffRegionZipCodeSlideOut";

export class TariffRegionZipCodeSlideOut extends AutogenLayoutTariffRegionZipCodeSlideOut {

    private _saveButton: Button;
    public set saveButton(value: Button) { this._saveButton = value; }
    public get saveButton() { return this._saveButton; }

    addZipCodeSlideout(regUID: any, tariffRegionDataSource: DataSource) {
        new EditRowDecorator({
            title: "Add Tariff Zip Code",
            layout: this,
            fillVerticalSpace: true,
            width: '50%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.addUpdateTariffZipCode(true, regUID, tariffRegionDataSource),
            layoutLoadListeners: async () => {
                this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await this.mainDataSource.createBlankRow()]);
                this.activeRow.set("reg_uid", regUID);
                this.activeRow.set("effective_timestamp", new Date());
            },
            doAfterSlideIn: () => {
                this.textboxFdZipcode.enabled = true;
            },
            onClose: async (cancelled: boolean) => {
                if (cancelled && this.mainDataSource.hasChanged()) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true) {
                        return;
                    }
                }
                this.slideOut();
                tariffRegionDataSource.search({ reg_uid: regUID });
            },
        });
    }

    updateZipCodeSlideout(regUID: any, tariffRegionDataSource: DataSource, modelRow: ModelRow) {
        new EditRowDecorator({
            title: "Update Tariff Zip Code - " + modelRow.get("fd_zipcode"),
            layout: this,
            fillVerticalSpace: true,
            width: '50%',
            data: modelRow,
            dataSource: this.mainDataSource,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.addUpdateTariffZipCode(false, regUID, tariffRegionDataSource),
            doAfterSlideIn: () => {
                this.textboxFdZipcode.enabled = false;
                this.textboxStateId.enabled = false;
                const stateId = modelRow.get("state_id", null);
                const options: DropdownItem[] = [];
                options.push(new DropdownItem(stateId, stateId));
                this.textboxStateId.items = options;
                this.textboxStateId.selectedItem = options[0];
            },
            onClose: async (cancelled: boolean) => {
                if (cancelled && this.mainDataSource.hasChanged()) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave !== true) {
                        return;
                    }
                }
                this.slideOut();
                tariffRegionDataSource.search({ reg_uid: regUID });
            },
        });
    }

    addUpdateTariffZipCode(isAddMode: boolean, regUID: any, tariffRegionDataSource: DataSource): Button {
        const caption = isAddMode ? "Add" : "Update";
        const addUpdateButton = new Button({
            backgroundColor: "primary",
            borderColor: "primary",
            borderWidth: 1,
            caption: caption,
            color: "primary.reverse",
            rowBreak: false,
            onClick: async () =>  {
                try {
                    addUpdateButton.busy = true;
                    const isDataSourceChanged = this.mainDataSource.hasChanged();
                    if (isDataSourceChanged === false) {
                        CommonDialogs.showError("No changes were made. Please try again or check your input.");
                        return;
                    }
                    const activeRow = this.mainDataSource.activeRow
                    if (!this.checkRequiredField(activeRow.get("fd_zipcode"), "Zip Code")) return;
                    if (!this.checkRequiredField(activeRow.get("state_id"), "State ID")) return;
                    if (!this.checkRequiredField(activeRow.get("effective_timestamp"), "Effective Timestamp")) return;

                    const uniqueID: string = activeRow.get("rxz_uid", null);
                    const effectiveTimestamp = activeRow.get("effective_timestamp", null);
                    const expireTimestamp = activeRow.get("expire_timestamp", null);
                    const activeEffectiveDate = Date.parse(effectiveTimestamp);
                    const activeExpireDate = expireTimestamp != null ? Date.parse(expireTimestamp) : null;
                    const response = await Api.search("lme/dispatch/tariff-zip-location-validation", {"reg_uid": regUID, "unique_id": uniqueID, "is_zip": true});
                    if (response) {
                        const data = response.data[0].response_data;
                        if (data && data.length > 0) {
                            for (const row of data) {
                                const zipCodeFromList = row.fd_zipcode;
                                if (zipCodeFromList === activeRow.get("fd_zipcode")) {
                                    const listEffectiveTimestamp = row.effective_timestamp;
                                    const listExpireTimestamp = row.expire_timestamp;
                                    const listEffectiveDate = Date.parse(listEffectiveTimestamp);
                                    const listExpireDate = listExpireTimestamp != null ? Date.parse(listExpireTimestamp) : null;
                                    const activeHasExpire = activeExpireDate != null;
                                    const listHasExpire = listExpireDate != null;
                                    const activeEndsBeforeListStarts = activeHasExpire && activeExpireDate <= listEffectiveDate;
                                    const listEndsBeforeActiveStarts = listHasExpire && listExpireDate <= activeEffectiveDate;
                                    const isNonOverlapping = activeEndsBeforeListStarts || listEndsBeforeActiveStarts;
                                    if (!isNonOverlapping) {
                                        CommonDialogs.showError(`Zip code ${zipCodeFromList} in ${activeRow.get("state_id")} has a conflicting effective row in the table.`);
                                        return
                                    }
                                }
                            }
                        }
                    }
                    this.postTariffZipCode(isAddMode, regUID, tariffRegionDataSource);
                } catch (error) {
                    CommonDialogs.showError(error);
                }
                finally {
                    addUpdateButton.busy = false;
                }
            }

        })
        return addUpdateButton;
    }

    private checkRequiredField(data: string, value: string) {
        if (data == null) {
            CommonDialogs.showError(value + " is required.");
            return false;
        }
        return true;
    }

    private async postTariffZipCode( isAddMode: boolean, regUID: any, tariffRegionDataSource: DataSource) {
        try {
            const response = await this.mainDataSource.post();
            if (response) {
                const message = isAddMode ? "Tariff Zip Code Added" : "Tariff Zip Code Updated";
                Toast.showToast(message);
                await this.delay(2000);
                this.slideOut();
                await tariffRegionDataSource.search({ reg_uid: regUID });
            }
        } catch (error) {
            CommonDialogs.showError(error);
        }
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async textboxFdZipcodeOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const zipCode = textbox.text.trim();
        if (StringUtil.isEmptyString(zipCode)) {
            this.activeRow.set("state_id", null);
            this.textboxStateId.items = [];
            this.textboxStateId.selectedItem = null;
            this.textboxStateId.enabled = false;
            return;
        }
        const usZipRegex = /^\d{5}(-\d{4})?$/;
        const caPostalRegex = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
        const isUsZip = usZipRegex.test(zipCode);
        const isCaPostal = caPostalRegex.test(zipCode);
        if (!isUsZip && !isCaPostal) {
            CommonDialogs.showError("Invalid Zip Code format. Please enter a valid US or Canadian zip code.");
            this.activeRow.set("state_id", null);
            this.textboxStateId.items = [];
            this.textboxStateId.selectedItem = null;
            this.textboxStateId.enabled = false;
            return;
        }
        const response = await Api.post("lme/general/state-from-zip", {zip_code: zipCode});
        const stateIds: string[] = response.data[0]?.state_id;
        if (stateIds == null || stateIds.length === 0) {
            CommonDialogs.showError("No state found for the provided zip code - " + zipCode);
            this.activeRow.set("state_id", null);
            this.textboxStateId.items = [];
            this.textboxStateId.selectedItem = null;
            this.textboxStateId.enabled = false;
            return;
        }
        this.textboxStateId.enabled = true;
        const stateOptions: DropdownItem[] = stateIds.map(state => new DropdownItem(state, state));
        this.textboxStateId.items = stateOptions;
        this.textboxStateId.selectedItem = stateOptions[0];
        this.activeRow.set("state_id", stateOptions[0]?.value);
    }

    textboxExpireTimestampOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const effectiveDate = this.activeRow.get("effective_timestamp");
        const expireDate = textbox.getDataValue();
        if (effectiveDate != null && expireDate != null && Date.parse(expireDate) < Date.parse(effectiveDate)) {
            textbox.text = "";
            CommonDialogs.showError("Expire date cannot be before effective date.");
        }
    }
}
